import { useId, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { AdminSectionCard } from "@/components/admin/AdminSectionCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import {
  BELOW_ADMIN_REFUSAL,
  formatDays,
  judgeReason,
  judgeTrialDays,
  loadedAnnouncement,
  P1_TRIALS_DO_NOT_MOVE,
  P2_NOTHING_REACHES_STRIPE,
  P3_WHERE_THE_NUMBER_SHOWS,
  previewSentence,
  provenanceSentence,
  S8_DAY_ONE_WARNING,
} from "@/lib/trial-policy";
import {
  trialPolicySchema,
  type TrialPolicyFormInput,
  type TrialPolicyFormOutput,
} from "@/lib/schemas/trial-policy-schemas";
import { getTrialPolicy, updateTrialPolicy } from "@/services/trial-policy";

/**
 * The card that makes the Pro+ trial length editable.
 *
 * It sits on the plan-management screen, which is the Stripe-synced catalogue's screen — so
 * the design's whole problem is looking like it belongs there without looking like a save
 * reaches Stripe. Identical chrome, different interior: no pill, no badge, no status dot
 * anywhere on the card, a number where the neighbours have a table, and the page's only solid
 * commit button.
 */
export function TrialPolicyCard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN" || user?.role === "SUPER_ADMIN";
  const queryClient = useQueryClient();
  const daysHelpId = useId();
  const daysErrorId = useId();
  const reasonErrorId = useId();
  const [confirming, setConfirming] = useState(false);
  /**
   * Whether the operator has asked to save and been refused.
   *
   * The refusal messages are gated on `typed… !== ''` so that an error never appears on a field
   * nobody has typed in yet. That gate is right until the moment somebody presses Save on an empty
   * required field: at that point they HAVE asked, focus moves to the field, and a refusal with no
   * sentence is a silent one. This flag is the second half of that condition.
   */
  const [attempted, setAttempted] = useState(false);

  /**
   * The GET is **not issued below ADMIN**, and the reason is stronger than tidiness: the
   * server's 403 is not readable in a browser. `cors()` sets its headers after `await next()`
   * and `requireRole` throws before that, so the refusal arrives as `Failed to fetch` —
   * indistinguishable from the API being down. A card that discovered its own permissions
   * would render "something went wrong" to a support engineer who had done nothing wrong.
   */
  const policy = useQuery({
    queryKey: ["admin", "trial-policy"],
    queryFn: getTrialPolicy,
    enabled: isAdmin,
  });

  const form = useForm<TrialPolicyFormInput, unknown, TrialPolicyFormOutput>({
    resolver: zodResolver(trialPolicySchema),
    defaultValues: { durationDays: "", reason: "" },
  });

  // Two single-name `useWatch` calls, so each field's changes re-render on their own.
  // `defaultValues` above sets both to `''`, so these are strings and never `undefined`. The
  // `?? ''` fallbacks this used to carry were measured unreachable across the whole suite -- dead
  // defensiveness that also made the file miss 100% on branches.
  const typedDays = String(useWatch({ control: form.control, name: "durationDays" }));
  const typedReason = String(useWatch({ control: form.control, name: "reason" }));

  /**
   * **Messages come from `judgeTrialDays`, not from `formState.errors`.**
   *
   * React Hook Form's default `mode` is `onSubmit`, and none of this package's fourteen
   * `useForm` call sites sets it — so `formState.errors` is empty until Save is pressed, while
   * the preview below updates on every keystroke. Rendering messages from the schema would have
   * shown the operator a preview reading "Nothing would be saved" with no message naming the
   * bound, which is the opposite of what was approved and of what the requirement asks for.
   *
   * One verdict function drives the message AND the preview, so the two cannot disagree by
   * construction rather than by intention.
   */
  const daysVerdict = judgeTrialDays(typedDays);
  const reasonVerdict = judgeReason(typedReason);
  const canCommit = daysVerdict.kind === "ok" && reasonVerdict.ok;
  /**
   * One expression per field for "is a refusal showing", used by the paragraph, by `aria-invalid`
   * and by `aria-describedby` alike.
   *
   * `aria-describedby` used to be derived separately — `reasonVerdict.ok ? undefined : reasonErrorId`
   * — which pointed at a paragraph the render gate had not drawn. A dangling reference resolves to
   * nothing, so the one reader who most needed the sentence got neither it nor any sign it existed.
   * Deriving all three from one expression is what makes that unrepresentable.
   */
  const showDaysError = daysVerdict.kind !== "ok" && (typedDays !== "" || attempted);
  const showReasonError = !reasonVerdict.ok && (typedReason !== "" || attempted);
  const current = policy.data?.durationDays;

  const save = useMutation({
    mutationFn: updateTrialPolicy,
    onSuccess: (written) => {
      queryClient.setQueryData(["admin", "trial-policy"], written);
      // The reason clears and the day field takes the saved value, so the preview's baseline
      // moves with it. No refusal message appears on the now-empty reason: it arrives on the
      // next keystroke, blur, or commit attempt, never on the save that just succeeded.
      form.reset({ durationDays: String(written.durationDays), reason: "" });
      // Cleared with the form: the next empty reason is untouched again, not refused-once.
      setAttempted(false);
    },
  });

  if (!isAdmin) {
    return (
      <AdminSectionCard
        title="Trial length"
        description="How long a new account's Pro+ trial lasts."
      >
        <div className="space-y-3 text-sm">
          {/* P1 renders here and P2 does not. P1 is true of the product whoever is reading;
              P2 is about what saving does, and this reader cannot save. */}
          <p className="text-muted-foreground">{P1_TRIALS_DO_NOT_MOVE}</p>
          <p className="rounded-md bg-muted/40 p-3 text-muted-foreground" role="status">
            {BELOW_ADMIN_REFUSAL}
          </p>
        </div>
      </AdminSectionCard>
    );
  }

  const pendingDays = daysVerdict.kind === "ok" ? daysVerdict.days : null;

  return (
    <AdminSectionCard title="Trial length" description="How long a new account's Pro+ trial lasts.">
      {/* `xl:grid-cols-2` and not `sm:`. The approved drawing splits at an 820px CARD, and the
          card is the viewport minus 386 on this shell — so `sm:` would split at a 638px card
          and crush the form into half the width at any laptop between 1024 and 1200. `xl:`
          (1280 viewport, 894px card) is the nearest step above the drawn one. Below the split
          the DOM order is value, P1, P2, then the form, which is what the fragment draws. */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2 xl:gap-8">
        <div className="space-y-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Current
          </p>
          {/* The value sits on the card surface at 30px, NOT in a bordered box — the box is
              reserved for the preview, which is the division `ExtendTrialPanel` already draws.
              A number where the neighbouring cards have a table is one of the five devices
              that separate this card from the Stripe catalogue around it. */}
          <p
            className="text-3xl font-semibold tabular-nums"
            aria-busy={policy.isPending}
            data-testid="trial-policy-current"
          >
            {current === undefined ? "—" : formatDays(current)}
          </p>
          <p className="text-sm text-muted-foreground" data-testid="trial-policy-provenance">
            {policy.data === undefined ? "" : provenanceSentence(policy.data.lastChange)}
          </p>
          {/* P1 and P2 sit ABOVE the field, at rest, for every role that reaches the card —
              not in a tooltip and not only in the confirmation, because they have to be true
              before anything is typed. P1 is the owner's first constraint rendered. */}
          <p className="border-l-2 border-border pl-3 text-sm text-muted-foreground">
            {P1_TRIALS_DO_NOT_MOVE}
          </p>
          <p className="border-l-2 border-border pl-3 text-sm text-muted-foreground">
            {P2_NOTHING_REACHES_STRIPE}
          </p>
          <p className="text-sm text-muted-foreground">{P3_WHERE_THE_NUMBER_SHOWS}</p>
        </div>

        <form
          noValidate
          className="space-y-4"
          onSubmit={form.handleSubmit(() => {
            setConfirming(true);
          })}
        >
          <div className="flex flex-col gap-1">
            <Label htmlFor="trial-policy-days">New length</Label>
            <div className="flex items-center gap-2">
              <Input
                id="trial-policy-days"
                type="number"
                inputMode="numeric"
                // `h-11 sm:h-10` and not `size="lg"`. The approved 44px target applies BELOW
                // the step only, `size="lg"` is unconditional and also `px-8`, and it cannot
                // reach an `Input` at all — `input.tsx` hard-codes `h-10` and `Input` has no
                // `size` prop. `cn` is tailwind-merge, so `h-11` resolves against that base.
                className="h-11 w-28 sm:h-10"
                aria-invalid={showDaysError}
                aria-describedby={showDaysError ? `${daysHelpId} ${daysErrorId}` : daysHelpId}
                disabled={save.isPending}
                {...form.register("durationDays")}
              />
              <span className="text-sm text-muted-foreground">days</span>
            </div>
            <p id={daysHelpId} className="text-xs text-muted-foreground">
              Between 1 and 90.
            </p>
            {showDaysError && (
              <p id={daysErrorId} className="text-xs text-destructive" role="alert">
                {daysVerdict.message}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="trial-policy-reason">Why are you changing it?</Label>
            <Input
              id="trial-policy-reason"
              className="h-11 sm:h-10"
              aria-invalid={showReasonError}
              aria-describedby={showReasonError ? reasonErrorId : undefined}
              disabled={save.isPending}
              {...form.register("reason")}
            />
            {showReasonError && (
              <p id={reasonErrorId} className="text-xs text-destructive" role="alert">
                {reasonVerdict.message}
              </p>
            )}
          </div>

          <div className="rounded-md border bg-muted/40 p-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              After saving
            </p>
            <p className="text-lg font-semibold tabular-nums" data-testid="trial-policy-preview">
              {pendingDays === null ? "—" : formatDays(pendingDays)}
            </p>
            <p className="text-sm text-muted-foreground">
              {pendingDays === null || current === undefined
                ? "Nothing would be saved."
                : previewSentence(current, pendingDays)}
            </p>
          </div>

          <Button
            type="submit"
            // `aria-disabled` and not `disabled` while a refusal is showing, so the control
            // stays in the tab order and the click handler can move focus to the field that
            // refused. `aria-disabled:opacity-50` because `button-variants` dims through
            // `disabled:opacity-50`, which does not fire on the aria attribute — without it the
            // button looks live. `pointer-events-none` must NOT be added: the handler is what
            // moves focus.
            //
            // In flight it is `aria-disabled` AND `aria-busy`, and the submit handler cannot
            // fire twice because `handleSubmit` is gated on `canCommit` below and the mutation
            // guard is the `save.isPending` check. Two audit rows for one change is the outcome
            // that guard exists to prevent.
            aria-disabled={!canCommit || save.isPending}
            aria-busy={save.isPending}
            className="aria-disabled:opacity-50"
            onClick={(event) => {
              if (!canCommit || save.isPending) {
                event.preventDefault();
                setAttempted(true);
                document
                  .getElementById(
                    daysVerdict.kind === "ok" ? "trial-policy-reason" : "trial-policy-days",
                  )
                  ?.focus();
              }
            }}
          >
            Save trial policy
          </Button>

          {save.isError && (
            <p className="text-xs text-destructive" role="alert">
              {save.error instanceof Error ? save.error.message : "The change was not saved."}
            </p>
          )}
          {save.isSuccess && (
            <p className="text-xs text-muted-foreground" role="status">
              Saved. New accounts get {formatDays(save.data.durationDays)} from now on.
            </p>
          )}
        </form>
      </div>

      {/* Spoken when the value arrives. DERIVED, not state set from an effect: the region is
          empty while the query is pending and gains text when the value lands, which is an
          update and therefore announced. An effect here drew the one lint warning this card
          would have cost, and the rule was right — `react.dev` says as much, and this package
          already has an infinite-render-loop story from setState-in-effect elsewhere. */}
      <p className="sr-only" aria-live="polite">
        {current === undefined ? "" : loadedAnnouncement(current)}
      </p>

      {confirming && pendingDays !== null && current !== undefined && (
        <Dialog open onOpenChange={setConfirming}>
          <DialogContent data-testid="trial-policy-confirm">
            <DialogHeader>
              <DialogTitle>Change the trial length for every future account?</DialogTitle>
              <DialogDescription>
                From {formatDays(current)} to {formatDays(pendingDays)}.
              </DialogDescription>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">Your reason: “{typedReason.trim()}”</p>
            {/* P1 repeated here, because the confirmation is the moment an operator is most
                likely to believe this reaches running trials. */}
            <p className="text-sm text-muted-foreground">{P1_TRIALS_DO_NOT_MOVE}</p>
            {pendingDays === 1 && (
              <p
                className="text-sm text-destructive"
                role="alert"
                data-testid="trial-policy-day-one"
              >
                {S8_DAY_ONE_WARNING}
              </p>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setConfirming(false);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  // The dialog closes BEFORE the request resolves, following
                  // `ClusterInvariantsPanel`. So the current value and the provenance line stay
                  // at the OLD value while the PUT is in flight, the card does not move
                  // optimistically, and nothing has to be rolled back on a refusal.
                  setConfirming(false);
                  save.mutate({ durationDays: pendingDays, reason: typedReason });
                }}
              >
                Save trial policy
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </AdminSectionCard>
  );
}
